import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ArrowLeft, Trash2, Building2, ChevronDown, ChevronRight, Pencil, FileText, MessageSquare, Check, Maximize2, Minimize2 } from 'lucide-react';
import InlineEditableField from '../components/crm/InlineEditableField';
import SocialShortcuts from '../components/crm/SocialShortcuts';
import ActivityTypeSelector from '../components/crm/ActivityTypeSelector';
import SkeletonCRMDetail from '../components/skeletons/SkeletonCRMDetail';
import RelationshipDrawer from '../components/crm/RelationshipDrawer';
import useObjectRecordDetail from '../hooks/useObjectRecordDetail';
import RichTextEditor from '../components/crm/RichTextEditor';
import { useDataObject } from './DataObjectSlugResolver';

// Map field_definition.field_type to InlineEditableField type
function getFieldEditType(fieldDef) {
  switch (fieldDef.field_type) {
    case 'select': return 'select';
    case 'multi_select': return 'tags';
    case 'boolean': return 'boolean';
    case 'url': return 'url';
    case 'number': return 'number';
    case 'date': return 'date';
    case 'object_relationship': return 'relationship';
    case 'user': return 'user';
    default: return 'text';
  }
}

function getFieldOptions(fieldDef) {
  if (fieldDef.field_type === 'select' && fieldDef.options?.choices) {
    return fieldDef.options.choices.map((c) => ({ value: c, label: c }));
  }
  return [];
}

function getResolvedDisplay(fd, resolvedData) {
  const resolved = resolvedData[fd.slug];
  if (!resolved || typeof resolved !== 'object') return undefined;
  return resolved.display_name || undefined;
}

// Determine display name from record data
function getRecordDisplayName(record, fieldDefinitions) {
  if (!record) return '';
  // Person: first_name + last_name
  const hasFirstName = fieldDefinitions.some(f => f.slug === 'first_name');
  const hasLastName = fieldDefinitions.some(f => f.slug === 'last_name');
  if (hasFirstName && hasLastName) {
    return `${record.first_name || ''} ${record.last_name || ''}`.trim();
  }
  // Fallback: name field
  if (record.name) return record.name;
  // Fallback: first required field
  const firstRequired = fieldDefinitions.find(f => f.is_required);
  return firstRequired ? (record[firstRequired.slug] || '') : '';
}

// Determine which fields to show in "primary" vs "expanded" groups
function splitFields(fieldDefinitions) {
  // Name fields are handled in the header, rich_text fields shown in right panel
  const nameFields = ['name', 'first_name', 'last_name'];
  const filtered = fieldDefinitions.filter(f => !nameFields.includes(f.slug) && f.field_type !== 'rich_text');
  // Show first 5 fields as primary, rest as expanded
  return {
    primary: filtered.slice(0, 5),
    expanded: filtered.slice(5),
  };
}

// Social URLs for SocialShortcuts
const SOCIAL_FIELDS = ['website', 'linkedin_url', 'instagram_url', 'twitter_url', 'facebook_url', 'tiktok_url'];

function getSocialProps(record, fieldDefinitions) {
  const hasSocials = fieldDefinitions.some(f => SOCIAL_FIELDS.includes(f.slug));
  if (!hasSocials || !record) return null;
  const props = {};
  for (const slug of SOCIAL_FIELDS) {
    props[slug] = record[slug] || undefined;
  }
  props.showWebsite = !!fieldDefinitions.find(f => f.slug === 'website');
  return props;
}

function ExpandedEditorModal({ field, richTextValues, richTextSaving, handleRichTextChange, onClose }) {
  const [animState, setAnimState] = useState('entering');

  useEffect(() => {
    // Trigger enter animation on next frame
    const raf = requestAnimationFrame(() => setAnimState('open'));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleClose = useCallback(() => {
    setAnimState('leaving');
    setTimeout(onClose, 200);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleClose]);

  const isVisible = animState === 'open';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ease-out"
        style={{ opacity: isVisible ? 1 : 0 }}
        onClick={handleClose}
      />
      {/* Modal panel */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden transition-all duration-200"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.97) translateY(8px)',
          transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center">
              <FileText className="w-3 h-3 text-gray-400" />
            </div>
            <span className="text-sm font-medium text-gray-700">{field.name}</span>
          </div>
          <div className="flex items-center gap-3">
            {richTextSaving[field.slug] === 'typing' && (
              <span className="text-[11px] text-gray-300 animate-pulse">Saving...</span>
            )}
            {richTextSaving[field.slug] === 'saved' && (
              <span className="text-[11px] text-emerald-400 flex items-center gap-1">
                <Check className="w-3 h-3" />
                Saved
              </span>
            )}
            <button
              onClick={handleClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close"
            >
              <Minimize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto expanded-rich-text-editor">
          <RichTextEditor
            content={richTextValues[field.slug] || ''}
            onChange={(html) => handleRichTextChange(field.slug, html)}
            placeholder="Write something..."
          />
        </div>
      </div>
    </div>
  );
}

export default function CRMRecordDetail() {
  const {
    record, resolvedData, objectDefinition, fieldDefinitions, loading,
    relationships, fetchRelatedRecords, createRecordLink, updateRecordLink, deleteRecordLink,
    ownerDisplay,
    getActivityTypeConfig,
    newNote, setNewNote, activityType, setActivityType,
    editingActivityId, editContent, setEditContent, editActivityType, setEditActivityType,
    addNote, deleteActivity, startEditActivity, cancelEditActivity, updateActivity,
    fetchRecord,
    handleFieldSave, deleteRecord, navigate,
    listPath, objectDefinitionId, recordId,
  } = useObjectRecordDetail(useDataObject()?.id);

  const [showAllFields, setShowAllFields] = useState(false);
  const [activeDrawerRelationship, setActiveDrawerRelationship] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [rightPanelView, setRightPanelView] = useState('activity');
  const [richTextValues, setRichTextValues] = useState({});
  const [richTextSaving, setRichTextSaving] = useState({});
  const [expandedEditorSlug, setExpandedEditorSlug] = useState(null);
  const richTextTimers = useRef({});

  // Editable name state
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [firstNameValue, setFirstNameValue] = useState('');
  const [lastNameValue, setLastNameValue] = useState('');


  // Sync name state from record
  React.useEffect(() => {
    if (record?.name) setNameValue(record.name);
    if (record?.first_name) setFirstNameValue(record.first_name);
    if (record?.last_name) setLastNameValue(record.last_name);
  }, [record?.name, record?.first_name, record?.last_name]);

  // Rich text fields (multiple)
  const richTextFields = fieldDefinitions.filter(f => f.field_type === 'rich_text');
  const hasDescription = richTextFields.length > 0;

  useEffect(() => {
    if (record && richTextFields.length > 0) {
      const values = {};
      for (const f of richTextFields) {
        values[f.slug] = record[f.slug] || '';
      }
      setRichTextValues(values);
    }
  }, [record, fieldDefinitions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRichTextChange = useCallback((slug, value) => {
    setRichTextValues(prev => ({ ...prev, [slug]: value }));
    setRichTextSaving(prev => ({ ...prev, [slug]: 'typing' }));
    if (richTextTimers.current[slug]) clearTimeout(richTextTimers.current[slug]);
    richTextTimers.current[slug] = setTimeout(async () => {
      const success = await handleFieldSave(slug, value);
      setRichTextSaving(prev => ({ ...prev, [slug]: success ? 'saved' : 'error' }));
      setTimeout(() => setRichTextSaving(prev => ({ ...prev, [slug]: null })), 1500);
    }, 800);
  }, [handleFieldSave]);

  useEffect(() => {
    const timers = richTextTimers.current;
    return () => {
      for (const key of Object.keys(timers)) {
        if (timers[key]) clearTimeout(timers[key]);
      }
    };
  }, []);

  // Escape key is handled inside ExpandedEditorModal with animated close

  const slug = objectDefinition?.slug;
  const isPerson = slug === 'person';
  const hasFirstLastName = fieldDefinitions.some(f => f.slug === 'first_name') && fieldDefinitions.some(f => f.slug === 'last_name');

  const { primary: primaryFields, expanded: expandedFields } = splitFields(fieldDefinitions);
  const socialProps = getSocialProps(record, fieldDefinitions);

  // --- Drawer helpers ---
  const openDrawer = (relationship) => {
    setActiveDrawerRelationship(relationship);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setDrawerVisible(true));
    });
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setTimeout(() => {
      setActiveDrawerRelationship(null);
    }, 200);
  };

  // Name save handlers
  const handleNameSave = () => {
    setEditingName(false);
    if (hasFirstLastName) {
      const fn = firstNameValue.trim();
      const ln = lastNameValue.trim();
      if (!fn || !ln) {
        setFirstNameValue(record?.first_name || '');
        setLastNameValue(record?.last_name || '');
        return;
      }
      if (fn !== record?.first_name || ln !== record?.last_name) {
        handleFieldSave('first_name', fn);
        handleFieldSave('last_name', ln);
      }
    } else {
      const trimmed = nameValue.trim();
      if (trimmed && trimmed !== record?.name) {
        handleFieldSave('name', trimmed);
      } else {
        setNameValue(record?.name || '');
      }
    }
  };

  if (loading) return <SkeletonCRMDetail />;
  if (!record) return null;

  const displayName = getRecordDisplayName(record, fieldDefinitions);
  const typeName = objectDefinition?.name || 'Record';

  return (
    <div className="px-8 py-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(listPath)}
          className="flex items-center text-sm text-gray-400 hover:text-gray-600 mb-5 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to {typeName}s
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            {/* Avatar / Icon */}
            {isPerson ? (
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-blue-600">
                  {record.first_name?.[0]}{record.last_name?.[0]}
                </span>
              </div>
            ) : (
              <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-brand-600" />
              </div>
            )}
            <div>
              {/* Editable name */}
              {editingName ? (
                hasFirstLastName ? (
                  <div className="flex space-x-2">
                    <input
                      autoFocus
                      type="text"
                      value={firstNameValue}
                      onChange={(e) => setFirstNameValue(e.target.value)}
                      onBlur={handleNameSave}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleNameSave();
                        if (e.key === 'Escape') { setEditingName(false); setFirstNameValue(record.first_name); setLastNameValue(record.last_name); }
                      }}
                      className="text-2xl font-semibold text-gray-900 border-b-2 border-brand-500 focus:outline-none bg-transparent w-40 tracking-tight"
                    />
                    <input
                      type="text"
                      value={lastNameValue}
                      onChange={(e) => setLastNameValue(e.target.value)}
                      onBlur={handleNameSave}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleNameSave();
                        if (e.key === 'Escape') { setEditingName(false); setFirstNameValue(record.first_name); setLastNameValue(record.last_name); }
                      }}
                      className="text-2xl font-semibold text-gray-900 border-b-2 border-brand-500 focus:outline-none bg-transparent w-40 tracking-tight"
                    />
                  </div>
                ) : (
                  <input
                    autoFocus
                    type="text"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    onBlur={handleNameSave}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleNameSave();
                      if (e.key === 'Escape') { setEditingName(false); setNameValue(record.name); }
                    }}
                    className="text-2xl font-semibold text-gray-900 border-b-2 border-brand-500 focus:outline-none bg-transparent tracking-tight"
                  />
                )
              ) : (
                <h1
                  className="text-2xl font-semibold text-gray-900 tracking-tight cursor-pointer hover:bg-gray-50 rounded-lg px-1.5 -mx-1.5 py-0.5 transition-colors"
                  onClick={() => setEditingName(true)}
                >
                  {displayName}
                </h1>
              )}
              {record.industry && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-50 text-gray-600 border border-gray-200 mt-1.5">
                  {record.industry}
                </span>
              )}
              {record.title && isPerson && (
                <p className="text-sm text-gray-500 mt-0.5">{record.title}</p>
              )}
              {socialProps && <SocialShortcuts {...socialProps} />}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {relationships.map((rel) => (
              <button
                key={rel.id}
                onClick={() => openDrawer(rel)}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {rel.display_name} {rel.count > 0 && <span className="text-brand-600 ml-1">{rel.count}</span>}
              </button>
            ))}
            {hasDescription && (
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setRightPanelView('activity')}
                  className={`p-1.5 rounded-md transition-all ${rightPanelView === 'activity' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                  title="Activity"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setRightPanelView('description')}
                  className={`p-1.5 rounded-md transition-all ${rightPanelView === 'description' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                  title="Description"
                >
                  <FileText className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <button
              onClick={deleteRecord}
              className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left - Details */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h2 className="text-base font-semibold text-gray-900 tracking-tight mb-3">Details</h2>
            <div className="divide-y divide-gray-100">
              {primaryFields.map((fd) => (
                <InlineEditableField
                  key={fd.id}
                  label={fd.name}
                  value={record[fd.slug]}
                  fieldName={fd.slug}
                  type={getFieldEditType(fd)}
                  options={getFieldOptions(fd)}
                  onSave={handleFieldSave}
                  placeholder={`Add ${fd.name.toLowerCase()}`}
                  displayValue={getResolvedDisplay(fd, resolvedData) || (fd.slug === 'owner_id' ? ownerDisplay : undefined)}
                  fieldDef={fd}
                />
              ))}
              {showAllFields && expandedFields.map((fd) => (
                <InlineEditableField
                  key={fd.id}
                  label={fd.name}
                  value={record[fd.slug]}
                  fieldName={fd.slug}
                  type={getFieldEditType(fd)}
                  options={getFieldOptions(fd)}
                  onSave={handleFieldSave}
                  placeholder={`Add ${fd.name.toLowerCase()}`}
                  displayValue={getResolvedDisplay(fd, resolvedData) || (fd.slug === 'owner_id' ? ownerDisplay : undefined)}
                  fieldDef={fd}
                />
              ))}
            </div>
            {expandedFields.length > 0 && (
              <button
                onClick={() => setShowAllFields(!showAllFields)}
                className="flex items-center text-sm text-brand-600 hover:text-brand-700 font-medium cursor-pointer mt-3"
              >
                {showAllFields ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                {showAllFields ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        </div>

        {/* Right - Activity Timeline or Description */}
        <div className="lg:col-span-2">
          {rightPanelView === 'description' && hasDescription ? (
            <div className="space-y-4">
              {richTextFields.map((field) => (
                <div key={field.id} className="bg-white rounded-xl border border-gray-100">
                  <div className="px-6 pt-5 pb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center">
                        <FileText className="w-3 h-3 text-gray-400" />
                      </div>
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">{field.name}</label>
                    </div>
                    <div className="h-5 flex items-center gap-2">
                      {richTextSaving[field.slug] === 'typing' && (
                        <span className="text-[11px] text-gray-300 animate-pulse">Saving...</span>
                      )}
                      {richTextSaving[field.slug] === 'saved' && (
                        <span className="text-[11px] text-emerald-400 flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Saved
                        </span>
                      )}
                      <button
                        onClick={() => setExpandedEditorSlug(field.slug)}
                        className="p-0.5 text-gray-300 hover:text-gray-500 transition-colors rounded"
                        title="Expand"
                      >
                        <Maximize2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <RichTextEditor
                    content={richTextValues[field.slug] || ''}
                    onChange={(html) => handleRichTextChange(field.slug, html)}
                    placeholder="Write something..."
                  />
                </div>
              ))}

              {/* Expanded editor modal — portaled to body for correct fixed positioning */}
              {expandedEditorSlug && (() => {
                const field = richTextFields.find(f => f.slug === expandedEditorSlug);
                if (!field) return null;
                return ReactDOM.createPortal(
                  <ExpandedEditorModal
                    field={field}
                    richTextValues={richTextValues}
                    richTextSaving={richTextSaving}
                    handleRichTextChange={handleRichTextChange}
                    onClose={() => setExpandedEditorSlug(null)}
                  />,
                  document.body
                );
              })()}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h2 className="text-base font-semibold text-gray-900 tracking-tight mb-5">Activity</h2>

              {/* Add Note */}
              <div className="mb-6">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Activity Type</label>
                  <ActivityTypeSelector value={activityType} onChange={setActivityType} />
                </div>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder={`Add a ${getActivityTypeConfig(activityType).label.toLowerCase()}...`}
                  rows="3"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={addNote}
                    disabled={!newNote.trim()}
                    className="px-4 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add {getActivityTypeConfig(activityType).label}
                  </button>
                </div>
              </div>

              {/* Timeline */}
              {(record.activities || []).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-10">No activity yet. Add a note above.</p>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-4 bottom-4 w-px border-l-2 border-gray-100" />
                  <div className="space-y-5">
                    {[...record.activities].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((activity) => {
                      const typeConfig = getActivityTypeConfig(activity.activity_type);
                      const IconComponent = typeConfig.icon;
                      const colorClasses = {
                        blue: 'bg-blue-50 text-blue-600',
                        green: 'bg-green-50 text-green-600',
                        purple: 'bg-purple-50 text-purple-600',
                        yellow: 'bg-amber-50 text-amber-600',
                        red: 'bg-red-50 text-red-600',
                      };
                      const colorClass = colorClasses[typeConfig.color] || colorClasses.blue;
                      const isEditing = editingActivityId === activity.id;
                      const isEdited = activity.updated_at && activity.created_at && new Date(activity.updated_at).getTime() - new Date(activity.created_at).getTime() > 1000;

                      return (
                        <div key={activity.id} className="flex space-x-3 group relative">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center relative z-10 ${colorClass}`}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0 pb-1">
                            <div className="flex items-center justify-between">
                              <div className="text-sm">
                                <span className="font-medium text-gray-900">{activity.user_name || 'Unknown'}</span>
                                <span className="text-gray-400 ml-2">
                                  · {typeConfig.label} · {new Date(activity.created_at).toLocaleString()}
                                </span>
                                {isEdited && (
                                  <span className="text-gray-300 ml-1 text-xs italic">(edited)</span>
                                )}
                              </div>
                              {!isEditing && (
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => startEditActivity(activity)}
                                    className="p-1 text-gray-400 hover:text-blue-500 rounded transition-colors"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => deleteActivity(activity.id)}
                                    className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                            {isEditing ? (
                              <div className="mt-2">
                                <div className="mb-2">
                                  <ActivityTypeSelector value={editActivityType} onChange={setEditActivityType} />
                                </div>
                                <textarea
                                  value={editContent}
                                  onChange={(e) => setEditContent(e.target.value)}
                                  rows="3"
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400"
                                />
                                <div className="flex justify-end gap-2 mt-2">
                                  <button
                                    onClick={cancelEditActivity}
                                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => updateActivity(activity.id)}
                                    disabled={!editContent.trim()}
                                    className="px-3 py-1 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{activity.content}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Relationship Drawer */}
      {activeDrawerRelationship && (
        <RelationshipDrawer
          relationship={activeDrawerRelationship}
          recordId={recordId}
          objectDefinitionId={objectDefinitionId}
          visible={drawerVisible}
          onClose={closeDrawer}
          fetchRelatedRecords={fetchRelatedRecords}
          createRecordLink={createRecordLink}
          updateRecordLink={updateRecordLink}
          deleteRecordLink={deleteRecordLink}
          fetchRecord={fetchRecord}
        />
      )}
    </div>
  );
}
